import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import RegionFilter from '../components/RegionFilter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  AlertTriangle,
  CheckCircle,
  FileText,
  Loader2,
  Download,
  Calendar,
  Building2,
  TrendingUp,
  AlertCircle,
  HelpCircle,
  ArrowLeft,
  ShieldAlert
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { getApiError } from '@/lib/utils';

export default function AuditFrequencyCompliancePage() {
  const { toast } = useToast();
  const { formatCurrency, currencySymbol, region } = useSettings();
  const { isAdmin, canManageSites, isScheduler } = useAuth();
  const navigate = useNavigate();
  
  // State - MUST be before any conditional returns for React hooks rules
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  // Filters - Default cutoff to today
  const today = new Date();
  const [cutoffDate, setCutoffDate] = useState(format(today, 'yyyy-MM-dd'));
  const [viewMode, setViewMode] = useState('overdue_only');
  
  // Status filter state (for clickable KPI tiles)
  const [statusFilter, setStatusFilter] = useState(null);
  const [regionId, setRegionId] = useState(null);
  const [includeDraftJobs, setIncludeDraftJobs] = useState(false);
  
  // Generate report - MUST be before conditional return
  const generateReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        cutoff_date: cutoffDate,
        view_mode: viewMode,
        region: region || undefined,
        region_id: regionId != null ? regionId : undefined,
        ...(( isAdmin || isScheduler) && includeDraftJobs ? { include_draft_jobs: true } : {})
      };
      
      const data = await reportsService.getAuditFrequencyCompliance(params);
      setReportData(data);
      
      if (data.sites.length === 0) {
        toast({
          title: 'No Results',
          description: 'No sites match the selected criteria. Try changing the view mode or check that sites have audit frequencies configured.',
          variant: 'default'
        });
      }
    } catch (err) {
      console.error('Failed to generate report:', err);
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to generate compliance report'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [cutoffDate, viewMode, region, regionId, includeDraftJobs, isAdmin, isScheduler, toast]);

  // Export PDF - MUST be before conditional return
  const exportPDF = useCallback(async () => {
    setExporting(true);
    try {
      const params = {
        cutoff_date: cutoffDate,
        view_mode: viewMode,
        region: region || undefined,
        region_id: regionId != null ? regionId : undefined,
        ...((isAdmin || isScheduler) && includeDraftJobs ? { include_draft_jobs: true } : {})
      };
      
      const blob = await reportsService.exportAuditFrequencyCompliancePDF(params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_frequency_compliance_${cutoffDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: 'Export Complete',
        description: 'PDF report downloaded successfully'
      });
    } catch (err) {
      console.error('Failed to export PDF:', err);
      toast({
        title: 'Export Failed',
        description: getApiError(err, 'Failed to export PDF'),
        variant: 'destructive'
      });
    } finally {
      setExporting(false);
    }
  }, [cutoffDate, viewMode, region, regionId, includeDraftJobs, isAdmin, isScheduler, toast]);
  
  // Access check - Admin, Scheduler, and Senior Scheduler can access (after all hooks)
  if (!canManageSites) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <ShieldAlert className="h-16 w-16 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h1>
        <p className="text-muted-foreground text-center mb-4">
          This report is only available to administrators and schedulers.
        </p>
        <Button onClick={() => navigate('/reports')} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Reports
        </Button>
      </div>
    );
  }

  // View mode labels
  const viewModeLabels = {
    'overdue_only': 'Overdue Sites Only',
    'all_active': 'All Active Sites',
    'no_history_only': 'Sites Without Audit History'
  };

  // Frequency display labels
  const frequencyLabels = {
    'monthly': 'Monthly (30 days)',
    'bi-monthly': 'Bi-Monthly (60 days)',
    'quarterly': 'Quarterly (90 days)',
    'semi-annual': 'Semi-Annual (182 days)',
    'annual': 'Annual (365 days)',
    'custom': 'Custom'
  };

  // Status badge styling
  const statusStyles = {
    'overdue': {
      bg: 'bg-red-100 text-red-800 border-red-200',
      icon: AlertTriangle,
      label: 'Overdue'
    },
    'on_track': {
      bg: 'bg-green-100 text-green-800 border-green-200',
      icon: CheckCircle,
      label: 'On Track'
    },
    'no_history': {
      bg: 'bg-amber-100 text-amber-800 border-amber-200',
      icon: HelpCircle,
      label: 'No History'
    },
    'scheduled': {
      bg: 'bg-blue-100 text-blue-800 border-blue-200',
      icon: Calendar,
      label: 'Scheduled'
    }
  };

  // Filter sites based on status filter
  const filteredSites = statusFilter && reportData
    ? reportData.sites.filter(site => site.status === statusFilter)
    : reportData?.sites || [];

  // Summary card component - now clickable for filtering
  const SummaryCard = ({ title, value, description, icon: Icon, variant = 'default', filterValue = null, isActive = false }) => {
    const variantStyles = {
      default: 'bg-slate-50 border-slate-200',
      danger: 'bg-red-50 border-red-200',
      success: 'bg-green-50 border-green-200',
      warning: 'bg-amber-50 border-amber-200',
      info: 'bg-blue-50 border-blue-200'
    };
    
    const isClickable = filterValue !== null;
    
    return (
      <Card 
        className={`${variantStyles[variant]} border ${isClickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${isActive ? 'ring-2 ring-indigo-500' : ''}`}
        onClick={() => {
          if (isClickable) {
            setStatusFilter(statusFilter === filterValue ? null : filterValue);
          }
        }}
      >
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className="text-2xl font-bold">{value}</p>
              {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
              {isClickable && <p className="text-xs text-indigo-600 mt-1">{isActive ? 'Click to clear filter' : 'Click to filter'}</p>}
            </div>
            {Icon && (
              <div className={`p-3 rounded-lg ${variant === 'danger' ? 'bg-red-100' : variant === 'success' ? 'bg-green-100' : variant === 'warning' ? 'bg-amber-100' : variant === 'info' ? 'bg-blue-100' : 'bg-slate-100'}`}>
                <Icon className={`h-5 w-5 ${variant === 'danger' ? 'text-red-600' : variant === 'success' ? 'text-green-600' : variant === 'warning' ? 'text-amber-600' : variant === 'info' ? 'text-blue-600' : 'text-slate-600'}`} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-frequency-compliance-page">
      <Toaster />
      
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/reports')}
            className="text-muted-foreground hover:text-foreground"
            data-testid="back-to-reports-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Reports
          </Button>
        </div>
      </div>
      
      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Calendar className="h-6 w-6 text-indigo-600" />
          Audit Frequency Compliance Report
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Identify sites that are overdue for audits based on their scheduled frequency
        </p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base">Report Parameters</CardTitle>
          <CardDescription>
            Select a cut-off date and view mode to generate the compliance report
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="cutoff-date">Cut-Off Date</Label>
              <Input
                id="cutoff-date"
                type="date"
                value={cutoffDate}
                onChange={(e) => setCutoffDate(e.target.value)}
                className="w-48"
                data-testid="cutoff-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="view-mode">View Mode</Label>
              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-56" data-testid="view-mode-select">
                  <SelectValue placeholder="Select view mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overdue_only">Overdue Sites Only</SelectItem>
                  <SelectItem value="all_active">All Active Sites</SelectItem>
                  <SelectItem value="no_history_only">Sites Without History</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <RegionFilter value={regionId} onChange={setRegionId} />

            {/* Include Draft Jobs — Admin/Scheduler only */}
            {(isAdmin || isScheduler) && (
              <div className="flex items-center gap-2 pb-1" data-testid="include-draft-jobs-toggle-wrap">
                <input
                  id="include-draft-jobs"
                  type="checkbox"
                  checked={includeDraftJobs}
                  onChange={e => setIncludeDraftJobs(e.target.checked)}
                  className="h-4 w-4 accent-amber-500 cursor-pointer"
                  data-testid="include-draft-jobs-checkbox"
                />
                <Label htmlFor="include-draft-jobs" className="text-sm cursor-pointer select-none">
                  Include draft jobs
                </Label>
              </div>
            )}
            
            <Button 
              onClick={generateReport} 
              disabled={loading}
              data-testid="generate-report-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
            
            {reportData && reportData.sites.length > 0 && (
              <Button 
                variant="outline" 
                onClick={exportPDF}
                disabled={exporting}
                data-testid="export-pdf-btn"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export PDF
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <>
          {/* Draft jobs warning banner */}
          {reportData.include_draft_jobs && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="draft-jobs-included-banner">
              <span className="font-semibold">DRAFT JOBS INCLUDED</span>
              <span className="text-amber-700">— Results include jobs from DRAFT, PENDING APPROVAL and RETURNED schedule batches. For planning purposes only; not for auditor or client distribution.</span>
            </div>
          )}
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4" data-testid="summary-cards">
            <SummaryCard
              title="Sites Reviewed"
              value={reportData.summary.total_sites_reviewed}
              icon={Building2}
            />
            <SummaryCard
              title="Overdue"
              value={reportData.summary.total_overdue}
              icon={AlertTriangle}
              variant="danger"
              filterValue="overdue"
              isActive={statusFilter === 'overdue'}
            />
            <SummaryCard
              title="On Track"
              value={reportData.summary.total_on_track}
              icon={CheckCircle}
              variant="success"
              filterValue="on_track"
              isActive={statusFilter === 'on_track'}
            />
            <SummaryCard
              title="Scheduled"
              value={reportData.summary.total_scheduled || 0}
              icon={Calendar}
              variant="info"
              filterValue="scheduled"
              isActive={statusFilter === 'scheduled'}
            />
            <SummaryCard
              title="No History"
              value={reportData.summary.total_no_history}
              icon={HelpCircle}
              variant="warning"
              filterValue="no_history"
              isActive={statusFilter === 'no_history'}
            />
            <SummaryCard
              title="Compliance Rate"
              value={`${reportData.summary.compliance_percentage}%`}
              icon={TrendingUp}
              variant={reportData.summary.compliance_percentage >= 80 ? 'success' : reportData.summary.compliance_percentage >= 50 ? 'warning' : 'danger'}
            />
            <SummaryCard
              title="Billing at Risk"
              value={formatCurrency(reportData.summary.total_billing_at_risk)}
              icon={AlertCircle}
              variant={reportData.summary.total_billing_at_risk > 0 ? 'danger' : 'default'}
            />
          </div>

          {/* Active Filter Indicator */}
          {statusFilter && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Filtering by:</span>
              <Badge variant="outline" className="capitalize">{statusFilter.replace('_', ' ')}</Badge>
              <Button variant="ghost" size="sm" onClick={() => setStatusFilter(null)}>
                Clear filter
              </Button>
            </div>
          )}

          {/* Results Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Compliance Details
                {statusFilter && <Badge variant="secondary" className="ml-2 capitalize">{statusFilter.replace('_', ' ')} only</Badge>}
              </CardTitle>
              <CardDescription>
                {viewModeLabels[viewMode]} as of {format(parseISO(cutoffDate), 'd MMMM yyyy')}
                {region && <span className="ml-2 px-2 py-0.5 bg-slate-100 rounded text-xs">{region}</span>}
                {statusFilter && <span className="ml-2">• Showing {filteredSites.length} of {reportData.sites.length} sites</span>}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredSites.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <HelpCircle className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg font-medium">No Sites Found</p>
                  <p className="text-sm mt-2">
                    {statusFilter 
                      ? `No sites with "${statusFilter.replace('_', ' ')}" status. Try clearing the filter.`
                      : 'No sites match the selected criteria. Try changing the view mode or check that sites have audit frequencies configured.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Site</TableHead>
                        <TableHead>Frequency</TableHead>
                        <TableHead className="text-center">Expected (Days)</TableHead>
                        <TableHead className="text-center">Last Audit</TableHead>
                        <TableHead className="text-center">Days Since</TableHead>
                        <TableHead className="text-center">Next Due / Scheduled</TableHead>
                        <TableHead className="text-center">Overdue By</TableHead>
                        <TableHead className="text-right">Billing ({currencySymbol})</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSites.map((site) => {
                        const statusConfig = statusStyles[site.status];
                        const StatusIcon = statusConfig?.icon || HelpCircle;
                        
                        return (
                          <TableRow 
                            key={site.site_id}
                            className={
                              site.status === 'overdue' ? 'bg-red-50' :
                              site.status === 'no_history' ? 'bg-amber-50' :
                              site.status === 'scheduled' ? 'bg-blue-50' :
                              ''
                            }
                            data-testid={`site-row-${site.site_id}`}
                          >
                            <TableCell>
                              <Badge className={`${statusConfig?.bg} border`}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusConfig?.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {site.client_colour && (
                                  <div 
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: site.client_colour }}
                                  />
                                )}
                                <span className="font-medium">{site.client_name}</span>
                              </div>
                            </TableCell>
                            <TableCell>{site.site_name}</TableCell>
                            <TableCell>
                              <span className="capitalize">{site.audit_frequency === 'custom' ? `Custom (${site.expected_interval_days}d)` : site.audit_frequency}</span>
                            </TableCell>
                            <TableCell className="text-center">{site.expected_interval_days}</TableCell>
                            <TableCell className="text-center">
                              {site.last_audit_date 
                                ? format(parseISO(site.last_audit_date), 'd MMM yyyy')
                                : <span className="text-muted-foreground">—</span>
                              }
                            </TableCell>
                            <TableCell className="text-center">
                              {site.days_since_last_audit !== null 
                                ? site.days_since_last_audit
                                : <span className="text-muted-foreground">—</span>
                              }
                            </TableCell>
                            <TableCell className="text-center">
                              {site.status === 'scheduled' && site.next_scheduled_date ? (
                                <span className="text-blue-600 font-medium">
                                  {format(parseISO(site.next_scheduled_date), 'd MMM yyyy')}
                                  <span className="text-xs ml-1">(Sched)</span>
                                </span>
                              ) : site.expected_next_audit_date ? (
                                format(parseISO(site.expected_next_audit_date), 'd MMM yyyy')
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {site.overdue_by_days > 0 
                                ? <span className="text-red-600 font-medium">{site.overdue_by_days}</span>
                                : <span className="text-muted-foreground">—</span>
                              }
                            </TableCell>
                            <TableCell className="text-right">
                              {site.forecast_price 
                                ? formatCurrency(site.forecast_price)
                                : <span className="text-muted-foreground">—</span>
                              }
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Initial State - No Report Generated */}
      {!reportData && !loading && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-600">Generate Compliance Report</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Select a cut-off date and view mode above, then click "Generate Report" to identify sites that are overdue for their scheduled audits.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
